import { TranslationKey, useTar } from '@/modules/i18n'
import { ChallengeService } from '@duck/api-definitions'
import { Button } from '@duck/ui'
import { useNavigate } from '@tanstack/react-router'

interface ErrorMessage {
    title: TranslationKey
    message: TranslationKey
    notified?: boolean
    retryable?: boolean
    goHome?: boolean
}

const errorMessages: Record<ChallengeService.ChallengeErrorCode, ErrorMessage> = {
    'unknownError': {
        title: 'errors.unknownError',
        message: 'errors.unknownErrorDescription',
        notified: true,
        retryable: true,
    },
    'challengeNotFound': {
        title: 'errors.challengeNotFound',
        message: 'errors.challengeNotFoundDescription',
    },
    'badRequest': {
        title: 'errors.challengeNotFound',
        message: 'errors.challengeNotFoundDescription',
        notified: true,
    },
    'invalidChallengeId': {
        title: 'errors.challengeNotFound',
        message: 'errors.challengeNotFoundDescription',
    },
    'challengeInCooldown': {
        title: 'errors.challengeInCooldown',
        message: 'errors.challengeInCooldownDescription',
    },
    'challengeNotOngoing': {
        title: 'errors.invalidChallenge',
        message: 'errors.invalidChallengeDescription',
        notified: true,
    },
    'databaseCreationFailed': {
        title: 'errors.databaseCreationFailed',
        message: 'errors.databaseCreationFailedDescription',
        notified: true,
        retryable: true,
    },
    'databasePopulationFailed': {
        title: 'errors.databasePopulationFailed',
        message: 'errors.databasePopulationFailedDescription',
        notified: true,
        retryable: true,
    },
    'databaseConnectionFailed': {
        title: 'errors.databaseConnectionFailed',
        message: 'errors.databaseConnectionFailedDescription',
        notified: true,
        retryable: true,
    },
    'taskNotFound': {
        title: 'errors.challengeNotFound',
        message: 'errors.challengeNotFoundDescription',
    },
    'unauthorized': {
        title: 'errors.unauthorized',
        message: 'errors.unauthorizedDescription',
    },
    'loggedOut': {
        title: 'errors.loggedOut',
        message: 'errors.loggedOutDescription',
    },
    'invalidAssignment': {
        title: 'errors.invalidAssignment',
        message: 'errors.invalidAssignmentDescription',
    },
    'queryTimeout': {
        title: 'errors.queryTimeout',
        message: 'errors.queryTimeoutDescription',
        notified: false,
        retryable: false,
    },
    'disallowedPatterns': {
        title: 'errors.disallowedPatterns',
        message: 'errors.disallowedPatternsDescription',
        notified: false,
        retryable: false,
    },
    'timeWindowEnded': {
        title: 'errors.timeWindowEnded',
        message: 'errors.timeWindowEndedDescription',
        goHome: true,
    },
    'sandboxNotSubmittable': {
        title: 'errors.sandboxNotSubmittable',
        message: 'errors.sandboxNotSubmittableDescription',
        goHome: true,
    },
}

export const challengeErrorsToNotify: ChallengeService.ChallengeErrorCode[] =
    Object.entries(errorMessages).filter(([_, error]) => error.notified).map(([key]) => key as ChallengeService.ChallengeErrorCode)

interface WorkspaceErrorProps {
    error: ChallengeService.ChallengeErrorCode
}

export const WorkspaceError = ({ error }: WorkspaceErrorProps) => {
    const { t } = useTar()
    const navigate = useNavigate()
    const { title, message, notified, retryable, goHome } = errorMessages[error]

    const handleRetry = () => {
        window.location.reload()
    }

    const handleGoHome = () => {
        navigate({ to: '/' })
    }

    return (
        <div className="flex flex-col justify-center pt-[23vh] items-center">
            {notified && (
                <p className="text-error text-lg mb-8">
                    {t('errors.criticalEventWeveBeenNotified')}
                </p>
            )}
            <div className="max-w-md mx-auto p-8 bg-error/10 border border-error/20 rounded-lg shadow-lg">
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                        <svg
                            className="h-8 w-8 text-error"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-medium text-foreground">
                            {t(title)}
                        </h3>
                        <p className="text-foreground mt-1">
                            {t(message)}
                        </p>
                    </div>
                </div>
            </div>
            {retryable && (
                <div className="mt-8 flex flex-col items-center justify-end">
                    <p className="text-foreground text-md me-4">
                        {t('errors.thisMightBeSolvedByRetrying')}
                    </p>
                    <Button onClick={handleRetry} variant="outline" className="mt-4">
                        {t('common.retry')}
                    </Button>
                </div>
            )}
            {goHome && (
                <div className="mt-8 flex flex-col items-center justify-end">
                    <Button onClick={handleGoHome} variant="outline" className="mt-4">
                        {t('common.goHome')}
                    </Button>
                </div>
            )}
        </div>
    )
}

